import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"
import { resizeEdgeAlongAxis, wouldSelfIntersect, polygonEdges, edgeLengthM } from "../editor/edges"

// Popover that opens over a plot-edge label (Stage 6d.2). User enters a
// new length in metres; apply re-shapes the polygon by sliding one of
// the edge's endpoints along the edge axis. Self-intersection is
// rejected with an inline error. Apply pushes an undo snapshot via
// setPlot(..., { snapshot: true }).
//
// Opened by the `plot:edge-click` custom event emitted from
// editor_controller's edge-label click handler. We listen at window
// scope so the popover can live anywhere in the DOM.
export default class extends Controller {
  static targets = ["popover", "input", "anchorToggle", "applyBtn", "error"]

  connect() {
    this._onEdgeClick = (e) => this.#open(e.detail)
    this._onKey = (e) => { if (e.key === "Escape" && this.#isOpen()) this.close() }
    this._onPlot = () => { if (this.#isOpen()) this.close() }
    window.addEventListener("plot:edge-click", this._onEdgeClick)
    window.addEventListener("keydown", this._onKey)
    // If the plot is swapped wholesale (new cadastre / manual replace) while
    // the popover is open, close it: the edge we were editing may not exist.
    editorStore.addEventListener("plot:change", this._onPlot)
    this.#close({ emit: false })
  }

  disconnect() {
    window.removeEventListener("plot:edge-click", this._onEdgeClick)
    window.removeEventListener("keydown", this._onKey)
    editorStore.removeEventListener("plot:change", this._onPlot)
    if (this._previewTimer) clearTimeout(this._previewTimer)
  }

  apply(e) {
    e?.preventDefault?.()
    if (!this.#isOpen()) return
    const raw = (this.inputTarget.value || "").replace(",", ".").trim()
    const newLen = parseFloat(raw)
    if (!isFinite(newLen) || newLen <= 0) {
      this.#showError("Введите положительное число")
      return
    }
    if (newLen > 10_000) {
      this.#showError("Слишком большое значение")
      return
    }
    const poly = editorStore.plot?.polygonM
    if (!poly) return
    const next = resizeEdgeAlongAxis(poly, this._edgeIdx, newLen, this._anchor)
    if (!next) { this.#showError("Не удалось пересчитать"); return }
    if (wouldSelfIntersect(next)) {
      this.#showError("Пересекает другую сторону")
      return
    }
    const nextPlot = {
      ...editorStore.plot,
      polygonM: next,
      // Annotate once so the UI can surface that the geometry diverges
      // from the upstream cadastre.
      source: editorStore.plot.source === "cadastre" ? "cadastre-edited" : editorStore.plot.source,
    }
    editorStore.setPlot(nextPlot, { snapshot: true })
    this.close()
  }

  close() { this.#close({ emit: true }) }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
    this.applyBtnTarget.disabled = false
    this.#schedulePreview()
  }

  toggleAnchor() {
    this._anchor = this._anchor === "a" ? "b" : "a"
    this.#renderAnchor()
    this.#schedulePreview()
  }

  #open({ edgeIdx, lenM, viewportX, viewportY }) {
    if (typeof edgeIdx !== "number") return
    this._edgeIdx = edgeIdx
    this._anchor = "b"
    this.popoverTarget.classList.remove("hidden")
    this.inputTarget.value = this.#formatLen(lenM)
    // Bypass clearError here — the scheduled preview would fire against the
    // original length and briefly flash a ghost identical to the real edge.
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
    this.applyBtnTarget.disabled = false
    this.#renderAnchor()
    // Position on the next frame: the popover was `display:none` until we
    // removed the `hidden` class above, so getBoundingClientRect() still
    // returns 0×0 until the browser lays it out. rAF gives the layout a
    // chance to run, so the real width/height feed the centre+clamp math.
    const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (cb) => setTimeout(cb, 16)
    raf(() => this.#position(viewportX, viewportY))
    // Defer focus a tick so iOS doesn't scroll the popover out of view.
    setTimeout(() => {
      this.inputTarget.focus({ preventScroll: true })
      this.inputTarget.select()
    }, 30)
  }

  #close({ emit }) {
    this.popoverTarget.classList.add("hidden")
    this._edgeIdx = null
    this._anchor = "b"
    this.#clearPreview()
    if (emit) this.dispatch("close", { bubbles: true })
  }

  // Debounced ghost preview: compute what the edge would look like at the
  // current input value and emit `edge-edit:preview` so the editor draws
  // a dashed ghost segment on top of the plot.
  #schedulePreview() {
    if (this._previewTimer) clearTimeout(this._previewTimer)
    this._previewTimer = setTimeout(() => this.#emitPreview(), 120)
  }

  #emitPreview() {
    if (!this.#isOpen()) return
    const raw = (this.inputTarget.value || "").replace(",", ".").trim()
    const newLen = parseFloat(raw)
    if (!isFinite(newLen) || newLen <= 0) { this.#clearPreview(); return }
    const poly = editorStore.plot?.polygonM
    if (!poly) return
    const next = resizeEdgeAlongAxis(poly, this._edgeIdx, newLen, this._anchor)
    if (!next || wouldSelfIntersect(next)) { this.#clearPreview(); return }
    window.dispatchEvent(new CustomEvent("edge-edit:preview", {
      detail: { edgeIdx: this._edgeIdx, nextPolygon: next },
    }))
  }

  #clearPreview() {
    if (this._previewTimer) { clearTimeout(this._previewTimer); this._previewTimer = null }
    window.dispatchEvent(new CustomEvent("edge-edit:preview", { detail: { nextPolygon: null } }))
  }

  #isOpen() {
    return !this.popoverTarget.classList.contains("hidden")
  }

  #position(viewportX, viewportY) {
    // Show first so getBoundingClientRect returns real dimensions.
    const rect = this.popoverTarget.getBoundingClientRect()
    const pad = 8
    const w = rect.width || 256
    const h = rect.height || 160
    // Prefer below the label; if it would clip, place above.
    const below = viewportY + 14
    const above = viewportY - h - 14
    const y = (below + h + pad > window.innerHeight && above > 60) ? above : below
    const desiredLeft = viewportX - w / 2
    const maxLeft = Math.max(pad, window.innerWidth - w - pad)
    const left = Math.max(pad, Math.min(maxLeft, desiredLeft))
    this.popoverTarget.style.top = `${y}px`
    this.popoverTarget.style.left = `${left}px`
  }

  #renderAnchor() {
    if (!this.hasAnchorToggleTarget) return
    this.anchorToggleTarget.textContent = this._anchor === "a" ? "начало ←" : "конец →"
    this.anchorToggleTarget.setAttribute(
      "aria-label",
      this._anchor === "a" ? "Сдвигать начало стороны" : "Сдвигать конец стороны",
    )
  }

  #showError(msg) {
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("hidden")
    this.applyBtnTarget.disabled = true
  }

  #formatLen(lenM) {
    let s
    if (lenM < 10) s = lenM.toFixed(2)
    else if (lenM < 100) s = lenM.toFixed(1)
    else s = String(Math.round(lenM))
    if (s.includes(".")) s = s.replace(/0+$/, "").replace(/\.$/, "")
    return s
  }
}

// Exported so the Stage 6d.2 tests can sanity-check what edges.js is
// doing without importing it from a third file.
export { polygonEdges, edgeLengthM }
